/**
 * Cabinet Comptable API Client
 * ISO 27001 A.9.2.3 - Management of privileged access rights
 */

import { fetchWithAuth, API_URL } from '@/utils/api';
import type {
  FirmClient,
  CabinetStats,
  InviteClientDTO,
  AddClientDTO,
  UpdateClientDTO,
  PaginatedFirmClients,
  FirmClientStatus,
  FirmClientPermissions,
} from '../types';

const API_BASE = `${API_URL}/cabinet`;

export interface ClientsQueryParams {
  page?: number;
  limit?: number;
  status?: FirmClientStatus;
  search?: string;
}

export const cabinetApi = {
  /**
   * Get all clients of the cabinet
   */
  async getClients(params: ClientsQueryParams = {}): Promise<PaginatedFirmClients> {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.set('page', params.page.toString());
    if (params.limit) queryParams.set('limit', params.limit.toString());
    if (params.status) queryParams.set('status', params.status);
    if (params.search) queryParams.set('search', params.search);

    const url = `${API_BASE}/clients${queryParams.toString() ? `?${queryParams}` : ''}`;
    const response = await fetchWithAuth(url);
    const data = await response.json();
    return data;
  },

  /**
   * Get a client by ID
   */
  async getClient(id: string): Promise<FirmClient> {
    const response = await fetchWithAuth(`${API_BASE}/clients/${id}`);
    const data = await response.json();
    return data.data;
  },

  /**
   * Invite a new client by email
   */
  async inviteClient(dto: InviteClientDTO): Promise<{
    id: string;
    invitationToken: string;
    invitedAt: string;
    clientName: string;
    clientEmail: string;
  }> {
    const response = await fetchWithAuth(`${API_BASE}/clients/invite`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(dto),
    });
    const data = await response.json();
    return data.data;
  },

  /**
   * Add an existing client (already on the platform)
   */
  async addExistingClient(dto: AddClientDTO): Promise<FirmClient> {
    const response = await fetchWithAuth(`${API_BASE}/clients/add`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(dto),
    });
    const data = await response.json();
    return data.data;
  },

  /**
   * Update a client
   */
  async updateClient(id: string, dto: UpdateClientDTO): Promise<FirmClient> {
    const response = await fetchWithAuth(`${API_BASE}/clients/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(dto),
    });
    const data = await response.json();
    return data.data;
  },

  /**
   * Suspend a client
   */
  async suspendClient(id: string): Promise<FirmClient> {
    const response = await fetchWithAuth(`${API_BASE}/clients/${id}/suspend`, {
      method: 'POST',
    });
    const data = await response.json();
    return data.data;
  },

  /**
   * Reactivate a suspended client
   */
  async reactivateClient(id: string): Promise<FirmClient> {
    const response = await fetchWithAuth(`${API_BASE}/clients/${id}/reactivate`, {
      method: 'POST',
    });
    const data = await response.json();
    return data.data;
  },

  /**
   * Archive a client
   */
  async archiveClient(id: string): Promise<FirmClient> {
    const response = await fetchWithAuth(`${API_BASE}/clients/${id}/archive`, {
      method: 'POST',
    });
    const data = await response.json();
    return data.data;
  },

  /**
   * Cancel a pending invitation
   */
  async cancelInvitation(id: string): Promise<void> {
    await fetchWithAuth(`${API_BASE}/clients/${id}`, {
      method: 'DELETE',
    });
  },

  /**
   * Get cabinet statistics
   */
  async getStats(): Promise<CabinetStats> {
    const response = await fetchWithAuth(`${API_BASE}/stats`);
    const data = await response.json();
    return data.data;
  },

  /**
   * Accept an invitation (for clients)
   */
  async acceptInvitation(token: string): Promise<FirmClient> {
    const response = await fetchWithAuth(`${API_BASE}/accept-invitation`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    });
    const data = await response.json();
    return data.data;
  },

  /**
   * Get firms managing the current client
   */
  async getMyFirms(): Promise<FirmClient[]> {
    const response = await fetchWithAuth(`${API_BASE}/my-firms`);
    const data = await response.json();
    return data.data;
  },

  /**
   * Check access permissions on a client
   */
  async checkAccess(clientTenantId: string): Promise<{
    hasAccess: boolean;
    permissions: FirmClientPermissions | null;
  }> {
    const response = await fetchWithAuth(`${API_BASE}/check-access/${clientTenantId}`);
    const data = await response.json();
    return data.data;
  },

  /**
   * Convert current tenant to accounting firm
   */
  async convertToFirm(): Promise<void> {
    await fetchWithAuth(`${API_BASE}/convert-to-firm`, {
      method: 'POST',
    });
  },

  // ===== DOSSIER CLIENT (Vue données) =====

  /**
   * Get client dossier summary
   */
  async getClientDossier(clientRelationId: string): Promise<ClientDossierSummary> {
    const response = await fetchWithAuth(`${API_BASE}/dossier/${clientRelationId}`);
    const data = await response.json();
    return data.data;
  },

  /**
   * Get client invoices
   */
  async getClientInvoices(clientRelationId: string): Promise<any[]> {
    const response = await fetchWithAuth(`${API_BASE}/dossier/${clientRelationId}/invoices`);
    const data = await response.json();
    return data.data;
  },

  /**
   * Get client expenses
   */
  async getClientExpenses(clientRelationId: string): Promise<any[]> {
    const response = await fetchWithAuth(`${API_BASE}/dossier/${clientRelationId}/expenses`);
    const data = await response.json();
    return data.data;
  },

  /**
   * Get client credit notes
   */
  async getClientCreditNotes(clientRelationId: string): Promise<any[]> {
    const response = await fetchWithAuth(`${API_BASE}/dossier/${clientRelationId}/credit-notes`);
    const data = await response.json();
    return data.data;
  },

  /**
   * Export client accounting data
   */
  async exportClientAccounting(
    clientRelationId: string,
    options: { format: 'FEC' | 'CSV'; startDate?: string; endDate?: string }
  ): Promise<ClientExportResult> {
    const response = await fetchWithAuth(`${API_BASE}/dossier/${clientRelationId}/export`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(options),
    });
    const data = await response.json();
    return data.data;
  },
};

// Types for dossier client
export interface ClientDossierSummary {
  client: {
    id: string;
    fullName?: string | null;
    companyName?: string | null;
    code: string | null;
    status: FirmClientStatus;
    startDate: string;
  };
  permissions: FirmClientPermissions;
  companies: Array<{
    id: string;
    legalName: string;
    registrationNumber: string | null;
  }>;
  invoices: {
    count: number;
    totalTTC: number | null;
  } | null;
  expenses: {
    count: number;
    totalTTC: number | null;
  } | null;
}

export interface ClientExportResult {
  clientName: string;
  clientCode: string | null;
  exportDate: string;
  format: 'FEC' | 'CSV';
  entriesCount: number;
  entries: any[];
}

export default cabinetApi;
